import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  CalendarMonth,
  CheckCircle
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { getAll } from '../../services/EmployeeService';
import PageHeader from '../../components/headers/Header';

const HiringReport = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState('all');

  const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1'];

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const data = await getAll();
      setEmployees(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableYears = () => {
    const years = employees.map(emp => new Date(emp.dateOfJoining).getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  };

  const getFilteredEmployees = () => {
    return employees.filter(emp => {
      const joinDate = new Date(emp.dateOfJoining);
      const year = joinDate.getFullYear();
      const month = joinDate.getMonth() + 1;

      if (year !== selectedYear) return false;
      if (selectedQuarter === 'all') return true;

      const quarter = Math.ceil(month / 3);
      return quarter === parseInt(selectedQuarter);
    });
  };

  const filteredEmployees = useMemo(() => getFilteredEmployees(), [employees, selectedYear, selectedQuarter]);

  const getMonthlyHiringData = () => {
    const monthlyData = Array.from({ length: 12 }, (_, i) => ({
      month: new Date(2024, i).toLocaleString('default', { month: 'short' }),
      hires: 0,
      active: 0,
      inactive: 0
    }));

    filteredEmployees.forEach(emp => {
      const month = new Date(emp.dateOfJoining).getMonth();
      monthlyData[month].hires++;
      if (emp.active) {
        monthlyData[month].active++;
      } else {
        monthlyData[month].inactive++;
      }
    });

    return monthlyData;
  };

  const monthlyData = useMemo(() => getMonthlyHiringData(), [filteredEmployees]);
  const departmentData = useMemo(() => {
    const deptData = {};
    filteredEmployees.forEach(emp => {
      const dept = emp.department?.name || 'Unassigned';
      deptData[dept] = (deptData[dept] || 0) + 1;
    });
    return Object.entries(deptData)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredEmployees]);

  const positionData = useMemo(() => {
    const posData = {};
    filteredEmployees.forEach(emp => {
      const pos = emp.position || 'Unknown';
      posData[pos] = (posData[pos] || 0) + 1;
    });
    return Object.entries(posData)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [filteredEmployees]);

  const officeData = useMemo(() => {
    const officeData = {};
    filteredEmployees.forEach(emp => {
      const office = emp.office?.name || 'Unknown';
      officeData[office] = (officeData[office] || 0) + 1;
    });
    return Object.entries(officeData).map(([name, value]) => ({ name, value }));
  }, [filteredEmployees]);

  const stats = useMemo(() => {
    const totalHires = filteredEmployees.length;
    const activeHires = filteredEmployees.filter(e => e.active).length;
    const inactiveHires = filteredEmployees.filter(e => !e.active).length;
    const retentionRate = totalHires > 0 ? ((activeHires / totalHires) * 100).toFixed(1) : 0;

    const monthsWithHires = monthlyData.filter(m => m.hires > 0).length;
    const avgPerMonth = monthsWithHires > 0 ? (totalHires / monthsWithHires).toFixed(1) : 0;

    const prevYearEmployees = employees.filter(emp => {
      const year = new Date(emp.dateOfJoining).getFullYear();
      return year === selectedYear - 1;
    });
    const trend = totalHires > prevYearEmployees.length;

    return { totalHires, activeHires, inactiveHires, retentionRate, avgPerMonth, trend };
  }, [filteredEmployees, monthlyData, selectedYear, employees]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh', boxSizing: 'border-box' }}>
      {/* Header */}
      <PageHeader
        title="HR Hiring Report"
        subtitle="Track and analyze hiring efforts across all periods"
      />

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {getAvailableYears().map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Quarter</InputLabel>
            <Select
              value={selectedQuarter}
              label="Quarter"
              onChange={(e) => setSelectedQuarter(e.target.value)}
            >
              <MenuItem value="all">All Year</MenuItem>
              <MenuItem value="1">Q1 (Jan-Mar)</MenuItem>
              <MenuItem value="2">Q2 (Apr-Jun)</MenuItem>
              <MenuItem value="3">Q3 (Jul-Sep)</MenuItem>
              <MenuItem value="4">Q4 (Oct-Dec)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            title: 'Total Hires', value: stats.totalHires, icon: <People sx={{ fontSize: 40, color: '#1976d2' }} />,
            footer: <><TrendingUp sx={{ color: '#2e7d32', mr: 0.5 }} /> Increasing trend</>, footerColor: '#2e7d32', showTrend: true
          },
          {
            title: 'Active Employees', value: stats.activeHires, icon: <CheckCircle sx={{ fontSize: 40, color: '#2e7d32' }} />,
            footer: `${stats.inactiveHires} inactive`, showTrend: false
          },
          {
            title: 'Retention Rate', value: `${stats.retentionRate}%`, icon: <TrendingUp sx={{ fontSize: 40, color: '#9c27b0' }} />,
            footer: 'Based on current status', showTrend: false
          },
          {
            title: 'Avg. Hires/Month', value: stats.avgPerMonth, icon: <CalendarMonth sx={{ fontSize: 40, color: '#ed6c02' }} />,
            footer: 'Per active month', showTrend: false
          }
        ].map((item, idx) => (
          <Grid item sx={{ width: '23%' }} key={idx}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">{item.title}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, mt: 1 }}>{item.value}</Typography>
                  </Box>
                  {item.icon}
                </Box>
                <Box mt={2}>
                  <Typography variant="body2" color={item.showTrend ? (stats.trend ? '#2e7d32' : '#d32f2f') : 'text.secondary'}>
                    {item.showTrend ? (stats.trend ? 'Increasing trend' : 'Decreasing trend') : item.footer}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Monthly Hiring Trend */}
        <Grid item sx={{ width: '49%' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Monthly Hiring Trend
              </Typography>
              <Box sx={{ flexGrow: 1, minHeight: 250, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="hires" stroke="#1976d2" strokeWidth={2} name="Total Hires" />
                    <Line type="monotone" dataKey="active" stroke="#2e7d32" strokeWidth={2} name="Still Active" />
                    <Line type="monotone" dataKey="inactive" stroke="#d32f2f" strokeWidth={2} name="Inactive" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Hiring by Office */}
        <Grid item sx={{ width: '48%' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Hiring by Office
              </Typography>
              <Box sx={{ flexGrow: 1, minHeight: 250, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={officeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {officeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Hiring by Department */}
        <Grid item sx={{ width: '49%' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Hiring by Department
              </Typography>
              <Box sx={{ flexGrow: 1, minHeight: 250, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={departmentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#1976d2" name="Hires" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top 5 Positions */}
        <Grid item sx={{ width: '48%' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Top 5 Positions Hired
              </Typography>
              <Box sx={{ flexGrow: 1, minHeight: 250, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={positionData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#2e7d32" name="Hires" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Hiring Summary Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Monthly Hiring Summary ({selectedYear})
            {selectedQuarter !== 'all' && ` - Q${selectedQuarter}`}
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {monthlyData.map((item, index) => (
                    <TableCell key={index} align="center" sx={{ fontWeight: 600, py: 1 }}>
                      {item.month}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {monthlyData.map((item, index) => (
                    <TableCell key={index} align="center" sx={{ py: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: item.hires > 0 ? '#e3f2fd' : '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          fontWeight: item.hires > 0 ? 600 : 400,
                          color: item.hires > 0 ? '#1976d2' : '#999',
                          fontSize: '1rem'
                        }}
                      >
                        {item.hires}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Detailed Hiring Records */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Detailed Hiring Records
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Office</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Join Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees
                  .sort((a, b) => new Date(b.dateOfJoining) - new Date(a.dateOfJoining))
                  .map((emp) => (
                    <TableRow key={emp.employeeId} hover>
                      <TableCell>{emp.firstName} {emp.lastName}</TableCell>
                      <TableCell>{emp.position}</TableCell>
                      <TableCell>{emp.department?.name || 'N/A'}</TableCell>
                      <TableCell>{emp.office?.name || 'N/A'}</TableCell>
                      <TableCell>
                        {new Date(emp.dateOfJoining).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={emp.active ? 'Active' : 'Inactive'}
                          color={emp.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HiringReport;